import type { JSX } from "react";
import { Button } from "../../../components";
import type { ReviewStepEvent } from "../../../data/chatQuery";
import { ReviewPlan } from "./ReviewPlan";
import { ReviewCard } from "./ReviewCard";

// [S-509b/e/h] Right-hand review pane: a multi-step clause walkthrough driven by
// the latest `review_step` payload. Composed from three reusable pieces —
// ReviewPlan (header + segmented progress), ReviewCard (the current clause + the
// fixed Approve·Keep·Type·Skip action bar + prev/next nav), and a footer of
// applied/kept/left pips. Controls call deterministic backend endpoints (onDecide
// / onGoto — see ChatThread); they're disabled while `pending`. The backend
// accepts any index and overwrites a prior decision, so navigating back to a
// decided clause and re-deciding "just works". `onTypeSubmit` sends a free-form
// instruction from the card's inline box, tagged with this clause.
export function ReviewPane({
  review,
  pending,
  onDecide,
  onGoto,
  onResolveSkips,
  onTypeSubmit,
  revising = false,
}: {
  review: ReviewStepEvent;
  pending: boolean;
  onDecide: (index: number, decision: "apply" | "keep" | "skip" | "undo") => void;
  onGoto: (index: number) => void;
  onResolveSkips: (choice: "review" | "finish") => void;
  onTypeSubmit: (index: number, name: string, text: string) => void;
  revising?: boolean;
}): JSX.Element {
  const { total, currentIndex, counts, status, steps, current } = review;
  // [S-509f] At the end-of-walkthrough "decide on your skips" point the pane
  // prompts (review them or finish?) instead of auto-looping to a skipped clause.
  const awaitingSkipReview = review.awaitingSkipReview === true;

  // [async-review] The plan is still building in the background (the session query
  // polls until it flips) — or the build failed (terminal). No clause plan exists
  // yet, so short-circuit before ReviewPlan/ReviewCard, which assume clauses.
  if (status === "building") {
    return (
      <aside
        className="sa-rev"
        aria-label="Clause-by-clause review"
        aria-busy="true"
        data-testid="review-pane-building"
      >
        <div className="sa-rvi__building">
          <span className="sa-rvi__building-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <span>Reviewing your draft — checking each clause against the certified standard…</span>
        </div>
      </aside>
    );
  }
  if (status === "failed") {
    return (
      <aside
        className="sa-rev"
        aria-label="Clause-by-clause review"
        data-testid="review-pane-failed"
      >
        <div className="sa-rvi__building">
          <span>
            {review.error ??
              "The review couldn't be built. Ask me to review it again."}
          </span>
        </div>
      </aside>
    );
  }
  return (
    <aside className="sa-rev" aria-label="Clause-by-clause review" data-testid="review-pane">
      <ReviewPlan steps={steps} currentIndex={currentIndex} total={total} />

      {awaitingSkipReview ? (
        <div className="sa-rev__body">
          <div className="sa-rev__skipprompt">
            <div className="sa-rev__skipprompt-text">
              You&rsquo;ve reviewed everything except {counts.skipped}{" "}
              clause{counts.skipped === 1 ? "" : "s"} you left to decide later.
            </div>
            <div className="sa-rev__acts">
              <Button
                variant="bare"
                className="sa-rev__btn sa-rev__btn--apply"
                disabled={pending}
                onClick={() => onResolveSkips("review")}
              >
                Review skipped
              </Button>
              <Button
                variant="bare"
                className="sa-rev__btn sa-rev__btn--keep"
                disabled={pending}
                onClick={() => onResolveSkips("finish")}
              >
                Finish review
              </Button>
            </div>
          </div>
        </div>
      ) : current !== null ? (
        <ReviewCard
          current={current}
          walked={steps.filter((s) => s.needsReview).map((s) => s.index)}
          pending={pending}
          decision={
            steps.find((s) => s.index === current.index)?.decision ?? "pending"
          }
          onDecide={onDecide}
          onGoto={onGoto}
          onTypeSubmit={onTypeSubmit}
          revising={revising}
        />
      ) : (
        <div className="sa-rev__body">
          <div className="sa-rev__done-note">
            {status === "complete"
              ? "All clauses reviewed — see the finalized draft in the chat."
              : "Loading the next clause…"}
          </div>
        </div>
      )}

      <div className="sa-rev__foot">
        <span className="sa-rev__pip">
          <span className="sa-rev__pip-dot is-applied" aria-hidden="true" />
          {counts.applied} applied
        </span>
        <span className="sa-rev__pip">
          <span className="sa-rev__pip-dot is-kept" aria-hidden="true" />
          {counts.kept} kept
        </span>
        <span className="sa-rev__pip">
          <span className="sa-rev__pip-dot is-left" aria-hidden="true" />
          {counts.left} left
        </span>
      </div>
    </aside>
  );
}
